import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { AnalyticsEvent, setAnalyticsVerificationMethod, useAnalytics } from '../../analytics';
import RegistrationPage from '../../components/layouts/RegistrationPage';
import PageHeader from '../../components/PageHeader';

import imIdL from '../../assets/svgs/imId-l.svg';
import chevronRight from '../../assets/svgs/chevron-right.svg';
import { checkImIdEnabled } from '../../utils/featureFlags';
import { cn } from '../../utils/cn';

const optionCardClass =
    'flex w-full min-h-[4.5rem] items-center justify-between gap-3 rounded-xl border border-custom-gray-200 bg-white px-4 py-4 text-left shadow-sm transition hover:border-custom-blue-primary/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue-primary';

const VerificationOptions = () => {
    const { t } = useTranslation();
    const { track } = useAnalytics();
    const isImIdEnabled = checkImIdEnabled();
    const navigate = useNavigate();

    const handleBack = () => navigate('/');

    const selectMethod = (verification_method: 'imID' | 'sumsub') => {
        setAnalyticsVerificationMethod(verification_method);
        track(AnalyticsEvent.VERIFICATION_METHOD_SELECTED, { verification_method });
    };

    return (
        <RegistrationPage headerProps={{ showBack: true, onBack: handleBack }}>
            <PageHeader
                title={t('verificationOptions.title')}
                align="start"
                className="mb-4 lg:mb-10 px-1"
            />
            <div
                className={cn('flex w-full flex-col gap-6 px-1 max-w-[40rem]')}
            >
                {isImIdEnabled && (
                    <div className="flex min-w-0 flex-1 flex-col gap-2 text-start">
                        <Link
                            to="/imid-verification"
                            className={optionCardClass}
                            onClick={() => selectMethod('imID')}
                        >
                            <img
                                src={imIdL}
                                alt=""
                                className="h-4 w-auto shrink-0 object-contain"
                            />
                            <img
                                src={chevronRight}
                                alt=""
                                width={24}
                                height={24}
                            />
                        </Link>
                        <p className="px-1 text-xs leading-relaxed text-custom-gray-600">
                            <Trans
                                i18nKey="verificationOptions.imIdDescription"
                                components={{
                                    bold: (
                                        <strong className="font-semibold text-custom-gray-800" />
                                    ),
                                }}
                            />
                        </p>
                    </div>
                )}

                <div
                    className={cn(
                        'flex min-w-0 flex-col gap-2 text-start',
                        isImIdEnabled ? 'flex-1' : 'w-full max-w-md'
                    )}
                >
                    <Link
                        to="/sumsub"
                        className={optionCardClass}
                        onClick={() => selectMethod('sumsub')}
                    >
                        <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-custom-gray-900 lg:text-base">
                            {t('verificationOptions.biometricIdentification')}
                        </span>
                        <img
                            src={chevronRight}
                            alt=""
                            width={24}
                            height={24}
                        />
                    </Link>
                    <p className="px-1 text-xs leading-relaxed text-custom-gray-600">
                        <Trans
                            i18nKey="verificationOptions.biometricDescription"
                            components={{
                                bold: (
                                    <strong className="font-semibold text-custom-gray-800" />
                                ),
                            }}
                        />
                    </p>
                </div>
            </div>
        </RegistrationPage>
    );
};

export default VerificationOptions;
